import React from "react";
import { Phone, Star } from "lucide-react";
import { site } from "../../mock";
import { DecorParticles, WhatsAppIcon } from "./Helpers";
import { CallbackButton } from "../PageBlocks";

const STATS = [
  { k: "35+", v: "années d'expérience" },
  { k: "98%", v: "satisfaction" },
  { k: "7j/7", v: "disponible" }
];

const KEYWORDS = ["Marabout Orléans","Voyant","Médium","Magie Blanche","Retour Affectif","Désenvoûtement","Guérisseur"];

export default function HeroSection() {
  return (
    <section className="hero-radial relative overflow-hidden">
      <DecorParticles />
      <div className="container-narrow relative py-14 md:py-28 grid md:grid-cols-2 gap-8 md:gap-12 items-center">
        <HeroContent />
        <HeroImage />
      </div>
      <KeywordStrip />
    </section>
  );
}

function HeroContent() {
  return (
    <div className="rise-in text-center md:text-left">
      <div className="mb-6 flex flex-col sm:flex-row items-center md:items-start justify-center md:justify-start gap-2">
        <Badge label="Cabinet au centre d'Orléans" />
        <Badge label="Déplacements dans tout le Loiret" />
      </div>
      <h1 data-testid="home-h1" className="mb-6">
        <span className="block font-display text-4xl md:text-6xl leading-[1.05] gold-gradient-text mb-4">Maître Diamma</span>
        <span className="block font-serif italic text-xl md:text-3xl text-gold-soft leading-snug font-normal">
          Grand Marabout & Sorcier puissant<br className="hidden sm:block" /> à Orléans — Voyant & Guérisseur
        </span>
      </h1>
      <p className="text-gold-soft/95 leading-relaxed mb-8 max-w-xl mx-auto md:mx-0">
        Là où d'autres échouent, Maître Diamma agit. Héritier d'une lignée sacrée de marabouts d'Afrique de l'Ouest, il consacre ses dons au <span className="text-gold">retour de l'être aimé</span>, à la magie blanche, au désenvoûtement et à la voyance juste. Consultations au centre d'Orléans, dans toute la métropole, le Loiret et les villes voisines.
      </p>
      <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-3 max-w-xl mx-auto md:mx-0">
        <CallbackButton id="home-hero" context="Accueil" className="md:text-base sm:basis-full" />
        <a href={`tel:${site.phoneRaw}`} className="btn-gold rounded-md px-4 sm:px-6 py-3 text-sm md:text-base flex items-center justify-center gap-2">
          <Phone className="w-4 h-4 shrink-0" /> <span className="whitespace-nowrap">{site.phone}</span>
        </a>
        <a href={site.whatsapp} target="_blank" rel="noreferrer" className="btn-ghost-gold rounded-md px-4 sm:px-6 py-3 text-sm md:text-base flex items-center justify-center gap-2">
          <WhatsAppIcon /> <span className="whitespace-nowrap">WhatsApp</span>
        </a>
      </div>
      <HeroStats />
    </div>
  );
}

function Badge({ label }) {
  return (
    <div className="inline-flex items-center gap-2 gold-border rounded-full px-3 py-1 text-[10px] md:text-[11px] uppercase tracking-[0.2em] md:tracking-[0.25em] text-gold-soft">
      <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--gold-300)]" /> {label}
    </div>
  );
}

function HeroStats() {
  return (
    <div className="mt-8 grid grid-cols-3 gap-3 max-w-lg">
      {STATS.map((s) => (
        <div key={s.k} className="text-center card-onyx rounded-lg py-3 px-2">
          <div className="text-xl md:text-3xl font-semibold text-gold">{s.k}</div>
          <div className="text-[10px] md:text-[11px] uppercase tracking-widest text-gold-deep leading-tight">{s.v}</div>
        </div>
      ))}
    </div>
  );
}

function HeroImage() {
  return (
    <div className="relative rise-in">
      <div className="absolute -inset-4 rounded-2xl gold-border opacity-40 rotate-3" />
      <div className="relative rounded-2xl overflow-hidden gold-border-strong gold-glow" style={{boxShadow:"0 30px 60px -20px rgba(212,168,56,0.35)"}}>
        <img
          src="/images/diamma-or.jpg"
          alt="Maître Diamma, grand marabout africain et sorcier puissant à Orléans"
          className="w-full h-[380px] md:h-[480px] object-cover object-top hero-image-mobile"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        <div className="absolute bottom-5 left-5 right-5">
          <div className="flex items-center gap-1 mb-2">
            {[...Array(5)].map((_, i) => <Star key={`hero-star-${i}`} className="w-4 h-4 text-gold" fill="currentColor" />)}
          </div>
          <p className="font-serif italic text-gold-soft text-lg">«La lumière revient toujours dans les cœurs qui la réclament.»</p>
          <p className="text-xs text-gold-deep mt-1">— Maître Diamma</p>
        </div>
      </div>
    </div>
  );
}

function KeywordStrip() {
  return (
    <div className="border-y border-[rgba(212,168,56,0.2)] bg-black/40">
      <div className="container-narrow py-4 flex flex-wrap justify-center gap-x-6 gap-y-2 text-[11px] uppercase tracking-[0.25em] text-gold-deep">
        {KEYWORDS.map((k, i) => (
          <React.Fragment key={k}>
            <span>{k}</span>
            {i < KEYWORDS.length - 1 && <span>•</span>}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
